import React, { useEffect, useRef, useState } from "react";
import { niceNumber, isValueAlmostZero } from "@/lib/util";
import { translate } from "@/modules/shared/IntlProviderWrapper";
import Tooltip from "@/modules/shared/ui/Tooltip";
import MaterialStockForecastTooltip from "./MaterialStockForecastTooltip";

import * as t from "@/lib/backend/manufacturing2.generated.types";
import * as sf from "@/lib/stockForecast";
import type { CellData } from "./MaterialStockForecastBody";
import img_arrow_up_stock from "@/img/arrow-up-stock.svg";
import img_arrow_down_stock from "@/img/arrow-down-stock.svg";
import img_icon_equal from "@/img/icon_equal.svg";

type Props = {
    current_value: number;
    prev_value: number | null;
    hide_details: boolean;
    hide_arrows: boolean;
    cellProps?: React.HTMLAttributes<HTMLTableCellElement>;
    getForecast?: (
        material_external_id: string,
        index: number,
        report_id: string
    ) => Promise<t.IGetMaterialForecastForShiftsRes>;
    cell_data: CellData;
    forecast_cell_css: string;
    report_id?: string;
};

const getArrowImage = (dir: number) => {
    let img_src: string | null = "";

    switch (Math.sign(dir)) {
        case 0:
            img_src = null;
            break;
        case 1:
            img_src = img_arrow_up_stock;
            break;
        case -1:
            img_src = img_arrow_down_stock;
            break;
        default:
            break;
    }

    return img_src;
};

const MaterialStockForecastCell = (props: Props) => {
    const { current_value, cellProps = {}, hide_arrows, getForecast, cell_data, report_id } = props;

    const [forecast, setForecast] = useState<t.IGetMaterialForecastForShiftsRes | null>(null);
    let forecast_cell_css = props.forecast_cell_css || "";

    const prev_value = props.prev_value || 0;

    const mountedRef = useRef(true);
    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
        };
    }, []);

    const isCombined = cell_data.selected_subtype === "combined";
    const has_tooltip = isCombined && getForecast !== undefined && report_id !== undefined;
    const openTooltip = async (e: React.MouseEvent<HTMLTableCellElement>) => {
        e.preventDefault();
        e.stopPropagation();
        if (has_tooltip && forecast === null && getForecast && report_id) {
            const res = await getForecast(cell_data.material_external_id, cell_data.idx, report_id);
            if (!mountedRef.current) {
                return;
            }
            setForecast(res);
        }
    };

    let arrow_img = getArrowImage(hide_arrows ? 0 : Math.round(current_value) - Math.round(prev_value));

    const nonCummulative = [
        "produced_by_shift",
        "consumed_by_shift",
        "received_by_shift",
        "required_by_shift",
    ].includes(cell_data.selected_subtype as sf.MaterialForecastSubtype);

    /*
        rules:
            cummulative - hide if current equals previous value
            noncummulative - hide if current equals zero
    */

    if (nonCummulative && Math.round(current_value) === 0) {
        return <td title={niceNumber(current_value, 0)} {...cellProps} className={forecast_cell_css}></td>;
    }

    let formatted_current_value = niceNumber(current_value, 0);

    if (!nonCummulative && prev_value === current_value) {
        const td = (
            <td
                {...cellProps}
                title={
                    has_tooltip ? translate("common.click_for_more", "Click for more info") : formatted_current_value
                }
                className={forecast_cell_css}
                onClick={has_tooltip ? openTooltip : undefined}
            />
        );

        return has_tooltip ? (
            <Tooltip
                trigger="click"
                content_key={forecast === null ? "loading" : "loaded"}
                content={
                    <MaterialStockForecastTooltip cell_data={cell_data} forecast={forecast} prev_value={prev_value} />
                }
            >
                {td}
            </Tooltip>
        ) : (
            td
        );
    }
    // Check if current value is so close to zero that it should be shown as '0'
    if (arrow_img === null && isValueAlmostZero(current_value)) {
        arrow_img = img_icon_equal;
        formatted_current_value = "0";
        forecast_cell_css = "text-right material-stock-prediction-cell stock_available";
    }

    if (isValueAlmostZero(current_value)) {
        formatted_current_value = "0";
        forecast_cell_css = "text-right material-stock-prediction-cell stock_available";
    }

    const td = (
        <td
            {...cellProps}
            title={has_tooltip ? translate("common.click_for_more", "Click for more info") : formatted_current_value}
            className={forecast_cell_css}
            onClick={e => {
                if (has_tooltip) {
                    openTooltip(e);
                }
            }}
        >
            <span> {formatted_current_value} </span>
            {!hide_arrows && arrow_img && <img src={arrow_img} alt="" />}
        </td>
    );
    return has_tooltip ? (
        <Tooltip
            trigger="click"
            content_key={forecast === null ? "loading" : "loaded"}
            content={<MaterialStockForecastTooltip cell_data={cell_data} forecast={forecast} prev_value={prev_value} />}
        >
            {td}
        </Tooltip>
    ) : (
        td
    );
};

export default MaterialStockForecastCell;
